/**
 * The card readers a desk can send a payment to.
 *
 * One fetch per mount, shared by the POS, the Record Payment modal and the
 * enrollment wizard. `enabled` is the club's opt-in (settings.terminal_enabled);
 * a club that has not opted in never sees a reader option anywhere.
 */
import { useCallback, useEffect, useState } from "react";
import { listReaders, TerminalReader, TerminalLocation } from "../api/terminalAPI";

export interface TerminalReadersState {
  enabled: boolean;
  readers: TerminalReader[];
  location: TerminalLocation | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export function useTerminalReaders(token: string | null | undefined): TerminalReadersState {
  const [enabled, setEnabled] = useState(false);
  const [readers, setReaders] = useState<TerminalReader[]>([]);
  const [location, setLocation] = useState<TerminalLocation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await listReaders(token);
      if (res?.success) {
        setEnabled(!!res.terminal_enabled);
        setReaders(Array.isArray(res.readers) ? res.readers : []);
        setLocation(res.location || null);
        setError(null);
      } else {
        setError(res?.error || "Failed to load card readers");
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { enabled, readers, location, loading, error, reload };
}
